import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, Crown, Download, Loader2, TrendingUp, Users, Wallet, Truck,
  Package, LineChart as LineChartIcon,
} from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, apiError, fmt } from "@/lib/api";

const PRESETS = [
  { id: "today", label: "Today" }, { id: "week", label: "This Week" },
  { id: "month", label: "This Month" }, { id: "year", label: "This Year" },
  { id: "all", label: "All Time" }, { id: "custom", label: "Custom" },
];
const METHODS = [
  { id: "", label: "All methods" }, { id: "online", label: "Online" },
  { id: "cod", label: "COD" }, { id: "credit", label: "PAQVERO Credit" },
];
const BUCKETS = [
  { id: "auto", label: "Auto" }, { id: "day", label: "Day" }, { id: "week", label: "Week" },
  { id: "month", label: "Month" }, { id: "year", label: "Year" },
];
const PIE_COLORS = ["#10B981", "#F59E0B", "#38BDF8"];
const FLAG_STYLE = {
  dead: { label: "Dead", cls: "border-red-500/40 bg-red-500/10 text-red-400" },
  slow: { label: "Slow", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  low_margin: { label: "Low margin", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  negative_margin: { label: "Negative", cls: "border-red-500/40 bg-red-500/10 text-red-400" },
  uncosted: { label: "No cost", cls: "border-white/15 bg-white/5 text-neutral-400" },
};
const INPUT = "h-10 rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white focus:border-amber-500/60 focus:outline-none";

const pct = (n) => (n === null || n === undefined ? "—" : `${n}%`);

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-[#F1EDE3] px-3 py-2 text-xs">
      <p className="mb-1 font-bold text-neutral-300">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
      ))}
    </div>
  );
};

const Section = ({ title, icon: Icon, testid, onExport, link, children }) => (
  <section className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5" data-testid={testid}>
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-neutral-400">
        <Icon className="h-4 w-4 text-amber-400" /> {title}
      </h2>
      <div className="flex items-center gap-3">
        {link}
        {onExport && (
          <button onClick={onExport} data-testid={`${testid}-export`}
            className="flex h-8 items-center gap-1.5 rounded-full border border-white/15 px-3 text-[11px] font-bold text-neutral-300 hover:border-amber-500/50 hover:text-amber-300">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
        )}
      </div>
    </div>
    {children}
  </section>
);

export default function AdminAnalytics() {
  const [preset, setPreset] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [bucket, setBucket] = useState("auto");
  const [method, setMethod] = useState("");
  const [category, setCategory] = useState("");
  const [productQ, setProductQ] = useState("");
  const [sort, setSort] = useState("revenue");
  const [ov, setOv] = useState(null);
  const [prod, setProd] = useState(null);
  const [cust, setCust] = useState(null);
  const [recv, setRecv] = useState(null);
  const [supp, setSupp] = useState(null);
  const [del, setDel] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);

  const params = useCallback(() => {
    const p = new URLSearchParams({ preset });
    if (preset === "custom") { p.set("start", start); p.set("end", end); }
    return p;
  }, [preset, start, end]);

  const load = useCallback(() => {
    if (preset === "custom" && (!start || !end)) return;
    setLoading(true);
    const p = params();
    const pm = `&bucket=${bucket}&method=${method}`;
    Promise.all([
      api.get(`/admin/analytics/overview?${p}${pm}`),
      api.get(`/admin/analytics/products?${p}&method=${method}&category=${encodeURIComponent(category)}&q=${encodeURIComponent(productQ)}&sort=${sort}`),
      api.get(`/admin/analytics/customers?${p}&method=${method}`),
      api.get(`/admin/analytics/receivables`),
      api.get(`/admin/analytics/suppliers?${p}`),
      api.get(`/admin/analytics/delivery?${p}${pm}`),
    ]).then(([a, b, c, d, e, f]) => {
      setOv(a.data); setProd(b.data); setCust(c.data); setRecv(d.data); setSupp(e.data); setDel(f.data);
      setForbidden(false);
    }).catch((e) => {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error(apiError(e, "Couldn't load analytics"));
    }).finally(() => setLoading(false));
  }, [params, preset, start, end, bucket, method, category, productQ, sort]);
  useEffect(load, [load]);

  const downloadCsv = async (report, extra = "") => {
    try {
      const res = await api.get(`/admin/analytics/export?report=${report}&${params()}&bucket=${bucket}&method=${method}${extra}`,
        { responseType: "blob", timeout: 60000 });
      const match = (res.headers?.["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : `paqvero-${report}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e, "Export failed"));
    }
  };

  if (forbidden) {
    return (
      <div data-testid="analytics-forbidden" className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center">
        <Crown className="mx-auto mb-3 h-8 w-8 text-amber-500/60" />
        <p className="text-sm font-semibold text-white">Owner access only</p>
        <p className="mt-1 text-xs text-neutral-500">Analytics is restricted to the account owner.</p>
      </div>
    );
  }

  const t = ov?.totals;
  const warnings = [...(ov?.warnings || []), ...(del?.warnings || [])]
    .filter((w, i, arr) => arr.findIndex((x) => x.code === w.code) === i);
  const pieData = ov ? [
    { name: "Online", value: ov.payments.online.amount },
    { name: "COD", value: ov.payments.cod.amount },
    { name: "Credit", value: ov.payments.credit.amount },
  ].filter((d) => d.value > 0) : [];

  const kpi = (label, value, sub, testid, accent = "text-white") => (
    <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
      <p className={`mt-1 text-lg font-bold ${accent}`} data-testid={testid}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-neutral-600">{sub}</p>}
    </div>
  );

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
          <TrendingUp className="h-6 w-6 text-amber-400" /> Analytics
        </h1>
        <p className="mt-1 text-sm text-neutral-500">Business intelligence from live data — same definitions as the Business dashboard.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="an-filters">
        {PRESETS.map((p) => (
          <button key={p.id} onClick={() => setPreset(p.id)} data-testid={`an-preset-${p.id}`}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${preset === p.id ? "bg-amber-500 text-black" : "border border-white/10 text-neutral-400 hover:text-white"}`}>
            {p.label}
          </button>
        ))}
        {preset === "custom" && (
          <>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="an-start" className={INPUT} />
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="an-end" className={INPUT} />
          </>
        )}
        <select value={bucket} onChange={(e) => setBucket(e.target.value)} data-testid="an-bucket" className={INPUT}>
          {BUCKETS.map((b) => <option key={b.id} value={b.id}>{`Trend: ${b.label}`}</option>)}
        </select>
        <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="an-method" className={INPUT}>
          {METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        {ov && <span className="text-[11px] text-neutral-600" data-testid="an-range">{ov.range.start || "…"} → {ov.range.end || "…"}</span>}
      </div>

      {warnings.length > 0 && (
        <div className="space-y-1.5 rounded-2xl border border-amber-500/25 bg-amber-500/[0.05] p-4" data-testid="an-warnings">
          {warnings.map((w) => (
            <p key={w.code} className="flex items-start gap-2 text-xs text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w.message}
            </p>
          ))}
        </div>
      )}

      {loading && !ov ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : !ov ? null : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="an-kpis">
            {kpi("Total sales", fmt(t.total_sales), `${t.orders} orders`, "an-kpi-sales")}
            {kpi("Net sales", fmt(t.net_sales), `− ${fmt(t.discounts)} disc · ${fmt(t.refunds)} refunds`, "an-kpi-net")}
            {kpi("Avg order value", fmt(t.average_order_value), null, "an-kpi-aov")}
            {kpi("COGS", fmt(t.cogs), t.cogs_coverage_pct !== null ? `${t.cogs_coverage_pct}% units costed` : "no sales", "an-kpi-cogs")}
            {kpi("Gross profit", fmt(t.gross_profit), `${pct(t.gross_margin_pct)} margin`, "an-kpi-gross", t.gross_profit >= 0 ? "text-emerald-400" : "text-red-400")}
            {kpi("Delivery revenue", fmt(t.delivery_revenue), null, "an-kpi-delrev")}
            {kpi("Delivery cost", fmt(t.delivery_cost), t.delivery_cost_coverage_pct !== null ? `${t.delivery_cost_coverage_pct}% orders covered` : "—", "an-kpi-delcost")}
            {kpi("Operating expenses", fmt(t.expenses), null, "an-kpi-expenses")}
            {kpi("Net profit", fmt(t.net_profit), `${pct(t.net_margin_pct)} margin`, "an-kpi-netprofit", t.net_profit >= 0 ? "text-emerald-400" : "text-red-400")}
            {kpi("Paid", fmt(ov.payments.paid_amount), `${ov.payments.paid_orders} orders`, "an-kpi-paid")}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Section title={`Revenue & profit trend (${ov.bucket})`} icon={LineChartIcon} testid="an-trend"
              onExport={() => downloadCsv("trend")}
              link={<button onClick={() => downloadCsv("overview")} data-testid="an-export-overview" className="text-xs font-bold text-amber-400 hover:text-amber-300">KPIs CSV</button>}>
              {ov.trend.length === 0 ? <p className="text-xs text-neutral-600">No sales in this period.</p> : (
                <div className="h-64" data-testid="an-trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={ov.trend} margin={{ left: 0, right: 8, top: 8 }}>
                      <CartesianGrid stroke="#ffffff10" vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fill: "#737373", fontSize: 10 }} />
                      <YAxis tick={{ fill: "#737373", fontSize: 10 }} width={70}
                        tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                      <Tooltip content={<ChartTip />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="sales" name="Sales" stroke="#F59E0B" fill="#F59E0B22" strokeWidth={2} />
                      <Area type="monotone" dataKey="net_profit" name="Net profit" stroke="#10B981" fill="#10B98122" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Section>

            <Section title="Payment methods" icon={Wallet} testid="an-payments">
              {pieData.length === 0 ? <p className="text-xs text-neutral-600">No payments in this period.</p> : (
                <div className="h-64" data-testid="an-payments-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false} fontSize={11}>
                        {pieData.map((d, i) => <Cell key={d.name} fill={PIE_COLORS[i % 3]} />)}
                      </Pie>
                      <Tooltip content={<ChartTip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
                {[["Online", ov.payments.online, "text-emerald-400"], ["COD", ov.payments.cod, "text-amber-300"], ["Credit", ov.payments.credit, "text-sky-300"]].map(([l, d, cls]) => (
                  <div key={l} className="rounded-lg bg-black/40 py-2">
                    <p className="text-[10px] uppercase tracking-wider text-neutral-600">{l}</p>
                    <p className={`font-bold ${cls}`} data-testid={`an-pay-${l.toLowerCase()}`}>{fmt(d.amount)}</p>
                    <p className="text-[10px] text-neutral-600">{d.orders} orders</p>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          {prod && (
            <Section title="Product performance" icon={Package} testid="an-products"
              onExport={() => downloadCsv("products", `&category=${encodeURIComponent(category)}&q=${encodeURIComponent(productQ)}&sort=${sort}`)}
              link={<Link to="/admin/inventory" data-testid="an-products-link" className="text-xs font-bold text-amber-400 hover:text-amber-300">Inventory →</Link>}>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="an-category" className={INPUT}>
                  <option value="">All categories</option>
                  {prod.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input value={productQ} onChange={(e) => setProductQ(e.target.value)} placeholder="Search product/SKU…"
                  data-testid="an-product-search" className={INPUT} />
                <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="an-sort" className={INPUT}>
                  <option value="revenue">By revenue</option><option value="qty">By quantity</option>
                  <option value="profit">By profit</option><option value="margin">By margin</option>
                </select>
                <span className="flex flex-wrap gap-1.5 text-[10px] font-bold" data-testid="an-product-flags">
                  <span className="rounded-full border border-red-500/30 px-2 py-1 text-red-400">{prod.summary.dead} dead</span>
                  <span className="rounded-full border border-amber-500/30 px-2 py-1 text-amber-300">{prod.summary.slow} slow</span>
                  <span className="rounded-full border border-amber-500/30 px-2 py-1 text-amber-300">{prod.summary.low_margin} low margin</span>
                  <span className="rounded-full border border-red-500/30 px-2 py-1 text-red-400">{prod.summary.negative_margin} negative</span>
                  <span className="rounded-full border border-white/10 px-2 py-1 text-neutral-400">{prod.summary.uncosted} uncosted</span>
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-left text-xs" data-testid="an-products-table">
                  <thead>
                    <tr className="border-b border-white/[0.07] text-[10px] uppercase tracking-wider text-neutral-500">
                      <th className="py-2 pr-3">Product</th><th className="py-2 pr-3 text-right">Sold</th>
                      <th className="py-2 pr-3 text-right">Revenue</th><th className="py-2 pr-3 text-right">COGS</th>
                      <th className="py-2 pr-3 text-right">Profit</th><th className="py-2 pr-3 text-right">Margin</th>
                      <th className="py-2 pr-3 text-right">Stock</th><th className="py-2 pr-3 text-right">Cover</th>
                      <th className="py-2">Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prod.rows.map((r) => (
                      <tr key={r.sku} className="border-b border-white/[0.05]" data-testid={`an-product-${r.sku}`}>
                        <td className="max-w-[200px] truncate py-2 pr-3 text-neutral-300" title={r.product_name}>
                          {r.product_name} <span className="font-mono text-amber-400">{r.sku}</span>
                        </td>
                        <td className="py-2 pr-3 text-right text-neutral-300">{r.sold_qty}</td>
                        <td className="py-2 pr-3 text-right text-neutral-300">{fmt(r.revenue)}</td>
                        <td className="py-2 pr-3 text-right text-neutral-500">{r.cogs === null ? "—" : fmt(r.cogs)}</td>
                        <td className={`py-2 pr-3 text-right font-bold ${r.gross_profit === null ? "text-neutral-600" : r.gross_profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {r.gross_profit === null ? "—" : fmt(r.gross_profit)}
                        </td>
                        <td className="py-2 pr-3 text-right text-neutral-300">{r.margin_pct === null ? "—" : `${r.margin_pct}%`}</td>
                        <td className="py-2 pr-3 text-right text-neutral-400">{r.stock}</td>
                        <td className="py-2 pr-3 text-right text-neutral-500">{r.days_of_cover === null ? "—" : `${r.days_of_cover}d`}</td>
                        <td className="py-2">
                          <span className="flex flex-wrap gap-1">
                            {r.flags.map((f) => (
                              <span key={f} className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase ${FLAG_STYLE[f].cls}`}
                                data-testid={`an-flag-${f}-${r.sku}`}>{FLAG_STYLE[f].label}</span>
                            ))}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[11px] text-neutral-600">{prod.notes}</p>
            </Section>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            {cust && (
              <Section title="Customers" icon={Users} testid="an-customers"
                onExport={() => downloadCsv("customers")}
                link={<Link to="/admin/customers" data-testid="an-customers-link" className="text-xs font-bold text-amber-400 hover:text-amber-300">CRM →</Link>}>
                <div className="mb-3 grid grid-cols-3 gap-2 text-center text-xs">
                  {[["Active buyers", cust.totals.customers], ["New", cust.totals.new], ["Repeat", cust.totals.repeat],
                    ["Repeat rate", pct(cust.totals.repeat_rate_pct)], ["Avg sales/buyer", fmt(cust.totals.average_sales_per_customer)],
                    ["Top-5 share", pct(cust.totals.top5_concentration_pct)]].map(([l, v], i) => (
                    <div key={l} className="rounded-lg bg-black/40 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-neutral-600">{l}</p>
                      <p className="font-bold text-white" data-testid={`an-cust-${i}`}>{v}</p>
                    </div>
                  ))}
                </div>
                {cust.top_customers.length === 0 ? <p className="text-xs text-neutral-600">No customer sales in this period.</p> : (
                  <div className="space-y-1.5">
                    {cust.top_customers.map((c) => (
                      <Link to={`/admin/customers/${c.customer_id}`} key={c.customer_id} data-testid={`an-cust-row-${c.customer_id}`}
                        className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-xs hover:bg-white/[0.03]">
                        <span className="min-w-0 truncate text-neutral-300">{c.business_name || c.name}
                          <span className="text-neutral-600"> · {c.email}</span></span>
                        <span className="shrink-0 text-neutral-400">{c.orders} orders · <strong className="text-white">{fmt(c.sales)}</strong>
                          {c.share_pct !== null && <span className="text-neutral-600"> ({c.share_pct}%)</span>}</span>
                      </Link>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-[11px] text-neutral-600">{cust.notes}</p>
              </Section>
            )}

            {recv && (
              <Section title="Receivables & overdue credit" icon={Wallet} testid="an-receivables"
                onExport={() => downloadCsv("receivables")}
                link={<Link to="/admin/business" data-testid="an-receivables-link" className="text-xs font-bold text-amber-400 hover:text-amber-300">Business →</Link>}>
                <div className="mb-3 grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-4">
                  {[["Unpaid orders", recv.totals.unpaid_orders], ["Credit outstanding", recv.totals.credit_outstanding],
                    ["Credit overdue", recv.totals.credit_overdue], ["Total", recv.totals.total]].map(([l, v], i) => (
                    <div key={l} className="rounded-lg bg-black/40 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-neutral-600">{l}</p>
                      <p className={`font-bold ${i === 2 && v > 0 ? "text-red-400" : i === 3 ? "text-amber-300" : "text-white"}`}
                        data-testid={`an-recv-${i}`}>{fmt(v)}</p>
                    </div>
                  ))}
                </div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Unpaid order aging (days)</p>
                <div className="mb-3 grid grid-cols-4 gap-2 text-center text-xs" data-testid="an-order-aging">
                  {recv.order_aging.map((b) => (
                    <div key={b.bucket} className="rounded-lg bg-black/40 py-2">
                      <p className="text-[10px] text-neutral-600">{b.bucket}</p>
                      <p className="font-bold text-white">{fmt(b.amount)}</p>
                    </div>
                  ))}
                </div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Credit overdue aging (days past due)</p>
                <div className="grid grid-cols-4 gap-2 text-center text-xs" data-testid="an-credit-aging">
                  {recv.credit_overdue_aging.map((b) => (
                    <div key={b.bucket} className="rounded-lg bg-black/40 py-2">
                      <p className="text-[10px] text-neutral-600">{b.bucket}</p>
                      <p className={`font-bold ${b.amount > 0 ? "text-red-400" : "text-white"}`}>{fmt(b.amount)}</p>
                    </div>
                  ))}
                </div>
                {recv.orders.length > 0 && (
                  <div className="mt-3 space-y-1 border-t border-white/[0.07] pt-3">
                    {recv.orders.slice(0, 5).map((o) => (
                      <p key={o.order_id} className="text-xs text-neutral-400" data-testid={`an-recv-order-${o.order_id}`}>
                        <span className="font-mono text-amber-400">{o.order_number}</span> · {o.customer} · {o.date} ·{" "}
                        <strong className="text-amber-300">{fmt(o.outstanding)}</strong> due · {o.age_days}d old
                      </p>
                    ))}
                  </div>
                )}
              </Section>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {supp && (
              <Section title="Supplier purchasing" icon={Truck} testid="an-suppliers"
                onExport={() => downloadCsv("suppliers")}
                link={<Link to="/admin/purchases" data-testid="an-suppliers-link" className="text-xs font-bold text-amber-400 hover:text-amber-300">Purchases →</Link>}>
                <p className="mb-3 text-xs text-neutral-500" data-testid="an-supp-totals">
                  {supp.totals.purchases} purchases · received {fmt(supp.totals.received_cost)} · in transit {fmt(supp.totals.ordered_not_received)} ·
                  paid {fmt(supp.totals.paid)} · <strong className="text-amber-300">outstanding {fmt(supp.totals.outstanding)}</strong>
                </p>
                {supp.suppliers.length === 0 ? <p className="text-xs text-neutral-600">No purchases in this period.</p> : (
                  <div className="space-y-1.5">
                    {supp.suppliers.map((s) => (
                      <div key={s.supplier_id} className="flex items-center justify-between gap-2 text-xs" data-testid={`an-supp-${s.supplier_id}`}>
                        <span className="min-w-0 truncate text-neutral-300">{s.supplier_name}</span>
                        <span className="shrink-0 text-neutral-500">{s.purchases} POs · <strong className="text-white">{fmt(s.received_cost)}</strong>
                          {s.outstanding > 0 && <span className="text-amber-300"> · due {fmt(s.outstanding)}</span>}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-[11px] text-neutral-600">{supp.notes}</p>
              </Section>
            )}

            {del && (
              <Section title="Delivery revenue vs cost" icon={Truck} testid="an-delivery"
                onExport={() => downloadCsv("delivery")}>
                <div className="mb-3 grid grid-cols-3 gap-2 text-center text-xs">
                  {[["Revenue", del.totals.delivery_revenue, "text-emerald-400"], ["Cost", del.totals.delivery_cost, "text-amber-300"],
                    ["Net", del.totals.net, del.totals.net >= 0 ? "text-emerald-400" : "text-red-400"]].map(([l, v, cls], i) => (
                    <div key={l} className="rounded-lg bg-black/40 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-neutral-600">{l}</p>
                      <p className={`font-bold ${cls}`} data-testid={`an-del-${i}`}>{fmt(v)}</p>
                    </div>
                  ))}
                </div>
                {del.trend.length === 0 ? <p className="text-xs text-neutral-600">No deliveries in this period.</p> : (
                  <div className="h-56" data-testid="an-delivery-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={del.trend} margin={{ left: 0, right: 8, top: 8 }}>
                        <CartesianGrid stroke="#ffffff10" vertical={false} />
                        <XAxis dataKey="bucket" tick={{ fill: "#737373", fontSize: 10 }} />
                        <YAxis tick={{ fill: "#737373", fontSize: 10 }} width={60} />
                        <Tooltip content={<ChartTip />} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="delivery_revenue" name="Revenue" fill="#10B981" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="delivery_cost" name="Cost" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <p className="mt-2 text-[11px] text-neutral-600">{del.notes}</p>
              </Section>
            )}
          </div>
        </>
      )}
    </div>
  );
}
